import { useNavigate } from 'react-router-dom';
import { Pencil, Share2, Trash2, Calendar, FileText, Building2, Layers } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { brandLabel, isAllBrand } from '@/lib/api';
import type { ReportListItem, ReportStatus } from '@/lib/api';

const DATE_FMT = new Intl.DateTimeFormat('vi-VN', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Asia/Ho_Chi_Minh',
});

function formatPeriodLabel(start: string, end: string): string {
  if (start === end) {
    const [y, m] = start.split('-');
    return `T${parseInt(m!, 10)}/${y}`;
  }
  const [ys, ms] = start.split('-');
  const [ye, me] = end.split('-');
  const startMonth = parseInt(ms!, 10);
  const endMonth = parseInt(me!, 10);
  if (ys === ye) {
    const startQ = Math.ceil(startMonth / 3);
    const endQ = Math.ceil(endMonth / 3);
    if (startQ === endQ && startMonth === (startQ - 1) * 3 + 1 && endMonth === startQ * 3) {
      return `Q${startQ} ${ys}`;
    }
  }
  return `${ms}/${ys} → ${me}/${ye}`;
}

const STATUS_STYLE: Record<ReportStatus, string> = {
  draft: 'bg-amber-500/[0.06] text-amber-800 border-amber-500/20',
  published: 'bg-teal-500/[0.06] text-teal-800 border-teal-500/25',
  archived: 'bg-muted/60 text-muted-foreground border-border',
};

const STATUS_LABEL: Record<ReportStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

export interface ReportCardProps {
  report: ReportListItem;
  onShare?: (report: ReportListItem) => void;
  onEdit?: (report: ReportListItem) => void;
  onDelete?: (report: ReportListItem) => void;
}

export function ReportCard({ report, onShare, onEdit, onDelete }: ReportCardProps) {
  const navigate = useNavigate();
  const periodLabel = formatPeriodLabel(report.period_start, report.period_end);

  // Use short id (first 8 chars of UUID) for prettier admin URLs.
  // Backend `/api/admin/reports/:id` resolves both short prefix and full UUID.
  const handleView = () => navigate(`/reports/${report.id.slice(0, 8)}`);

  // Wrap each action so the card-level click doesn't also fire when user hits an icon button.
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <Card
      onClick={handleView}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleView();
        }
      }}
      className="group flex cursor-pointer flex-col gap-3 p-4 transition-all hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {/* Top: badges */}
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="outline" className="gap-1">
          {isAllBrand(report.brand) ? <Layers className="h-3 w-3 text-primary" /> : <Building2 className="h-3 w-3" />}
          {brandLabel(report.brand, { short: true })}
        </Badge>
        <Badge variant="outline" className="gap-1">
          <Calendar className="h-3 w-3" />
          {periodLabel}
        </Badge>
        <Badge variant="outline" className={cn('ml-auto', STATUS_STYLE[report.status])}>
          {STATUS_LABEL[report.status]}
        </Badge>
      </div>

      {/* Title + description */}
      <div className="flex-1">
        <h3 className="line-clamp-2 text-sm font-semibold leading-tight transition-colors group-hover:text-primary">
          {report.title}
        </h3>
        {report.description && (
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{report.description}</p>
        )}
      </div>

      {/* Footer: metadata + actions */}
      <div className="flex items-center justify-between gap-2 border-t pt-3">
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <FileText className="h-3 w-3" />
          <span>Tạo: {DATE_FMT.format(new Date(report.created_at))}</span>
        </div>
        <div className="flex items-center gap-0.5">
          {onShare && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={stop(() => onShare(report))}
              disabled={report.status === 'draft'}
              title={report.status === 'draft' ? 'Phải Publish trước khi share' : 'Chia sẻ'}
            >
              <Share2 className="h-3.5 w-3.5" />
            </Button>
          )}
          {onEdit && (
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={stop(() => onEdit(report))} title="Chỉnh sửa">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {onDelete && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={stop(() => onDelete(report))}
              title="Xóa"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
